import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect } from 'react';
import { AppError } from '@/core/errors/AppError';
import { ClienteFormSchema, TIPOS_DOCUMENTO, } from '@/data/dto/cliente';
import { useCrearCliente, useEditarCliente } from '@/data/hooks/useClientes';
import { Button } from '@/shared/components/Button';
import { Field } from '@/shared/components/Field';
import { Input } from '@/shared/components/Input';
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, } from '@/shared/components/Modal';
import { DatePicker } from '@/shared/components/DatePicker';
import { PhoneInput } from '@/shared/components/PhoneInput';
import { Select } from '@/shared/components/Select';
import { Textarea } from '@/shared/components/Textarea';
import { useToast } from '@/shared/components/Toast';
const DEFAULT = {
    nombre: '',
    apellido: '',
    tipoDocumento: '',
    nroDocumento: '',
    email: '',
    telefono: '',
    fechaNacimiento: '',
    observaciones: '',
};
export function ClienteFormDialog({ open, onOpenChange, cliente }) {
    const modoEdicion = Boolean(cliente);
    const toast = useToast();
    const crear = useCrearCliente();
    const editar = useEditarCliente();
    const isPending = crear.isPending || editar.isPending;
    const form = useForm({
        resolver: zodResolver(ClienteFormSchema),
        defaultValues: DEFAULT,
    });
    useEffect(() => {
        if (!open)
            return;
        form.reset(cliente
            ? {
                nombre: cliente.nombre ?? '',
                apellido: cliente.apellido ?? '',
                tipoDocumento: (cliente.tipoDocumento ?? ''),
                nroDocumento: cliente.nroDocumento ?? '',
                email: cliente.email ?? '',
                telefono: cliente.telefono ?? '',
                fechaNacimiento: cliente.fechaNacimiento ?? '',
                observaciones: cliente.observaciones ?? '',
            }
            : DEFAULT);
    }, [open, cliente, form]);
    const onSubmit = form.handleSubmit((values) => {
        const onSuccess = () => {
            toast.exito(modoEdicion ? 'Cliente actualizado' : 'Cliente creado');
            onOpenChange(false);
        };
        const onError = (err) => {
            if (err instanceof AppError && err.codigo === 'CLIENTE_DUPLICADO') {
                form.setError('email', { message: 'Ya existe un cliente con estos datos.' });
                return;
            }
            toast.error(err instanceof AppError ? err.mensaje : 'No pudimos guardar el cliente.');
        };
        if (modoEdicion && cliente) {
            editar.mutate({ id: cliente.id, values }, { onSuccess, onError });
        }
        else {
            crear.mutate(values, { onSuccess, onError });
        }
    });
    const errors = form.formState.errors;
    return (_jsx(Modal, { open: open, onOpenChange: onOpenChange, children: _jsxs(ModalContent, { size: "md", children: [_jsx(ModalHeader, { titulo: modoEdicion ? 'Editar cliente' : 'Nuevo cliente', descripcion: "Los campos con * son obligatorios." }), _jsxs("form", { onSubmit: onSubmit, noValidate: true, children: [_jsx(ModalBody, { children: _jsxs("div", { className: "grid grid-cols-1 gap-4 sm:grid-cols-2", children: [_jsx(Field, { label: "Nombre", htmlFor: "nombre", error: errors.nombre?.message, required: true, children: _jsx(Input, { id: "nombre", autoFocus: true, error: Boolean(errors.nombre), ...form.register('nombre') }) }), _jsx(Field, { label: "Apellido", htmlFor: "apellido", error: errors.apellido?.message, children: _jsx(Input, { id: "apellido", ...form.register('apellido') }) }), _jsx(Field, { label: "Tipo de documento", htmlFor: "tipoDocumento", error: errors.tipoDocumento?.message, children: _jsxs(Select, { id: "tipoDocumento", ...form.register('tipoDocumento'), children: [_jsx("option", { value: "", children: "Sin especificar" }), TIPOS_DOCUMENTO.map((t) => (_jsx("option", { value: t, children: t }, t)))] }) }), _jsx(Field, { label: "N\u00FAmero", htmlFor: "nroDocumento", error: errors.nroDocumento?.message, children: _jsx(Input, { id: "nroDocumento", ...form.register('nroDocumento') }) }), _jsx(Field, { label: "Email", htmlFor: "email", error: errors.email?.message, children: _jsx(Input, { id: "email", type: "email", ...form.register('email') }) }), _jsx(Field, { label: "Tel\u00E9fono", htmlFor: "telefono", error: errors.telefono?.message, children: _jsx(PhoneInput, { id: "telefono", value: form.watch('telefono') ?? '', onChange: (v) => form.setValue('telefono', v, { shouldDirty: true }) }) }), _jsx(Field, { label: "Fecha de nacimiento", htmlFor: "fechaNacimiento", error: errors.fechaNacimiento?.message, children: _jsx(DatePicker, { id: "fechaNacimiento", modo: "birthdate", value: form.watch('fechaNacimiento') ?? '', onChange: (v) => form.setValue('fechaNacimiento', v, { shouldDirty: true }) }) }), _jsx(Field, { label: "Observaciones", htmlFor: "observaciones", error: errors.observaciones?.message, className: "sm:col-span-2", children: _jsx(Textarea, { id: "observaciones", rows: 3, ...form.register('observaciones') }) })] }) }), _jsxs(ModalFooter, { children: [_jsx(Button, { type: "button", variant: "secondary", onClick: () => onOpenChange(false), disabled: isPending, children: "Cancelar" }), _jsx(Button, { type: "submit", cargando: isPending, children: modoEdicion ? 'Guardar cambios' : 'Crear cliente' })] })] })] }) }));
}
